import { useCallback, useEffect, useRef, useState } from 'react';

import { feel } from './native-feel/index.js';
import { GlobeIcon } from './icons/index.js';

/**
 * One-tap language switcher — ONE TRUE source. Was about to be
 * triplicated across customer-pwa, marketing, and agent-pwa; extracted
 * to `@kilat/ui` so all callers share the same visual + interaction.
 *
 * **Decoupled from any specific i18n hook.** Each app owns its own
 * `useI18n` and passes the props in. That way the customer-pwa version
 * stays white-label-themed (CSS variables) while marketing + agent-pwa
 * use the Kilat tokens — same component, different brand context.
 *
 * Per [[feedback-locale-detection-and-switcher]]:
 *   - Pill with backdrop blur, sits over any background cleanly
 *   - Native-script labels (caller supplies the map)
 *   - First-launch nudge ribbon — appears once, dismisses on any
 *     interaction or explicit pick; copy is localized via the
 *     `nudgeLines` prop so the nudge speaks in the just-detected
 *     language ("Kami pilih Bahasa Malaysia daripada peranti anda…").
 *
 * Per [[feedback-premium-custom-ui]] / "no stock framework elements":
 *   custom-built pill + popover, no library dropdown.
 */

export interface LocaleSwitcherProps<L extends string> {
  /** Current locale, controlled by the caller. */
  readonly locale: L;
  /** Setter — caller persists / fires side effects as needed. */
  readonly setLocale: (next: L) => void;
  /** Locales the brand supports — drives the popover rows. */
  readonly supportedLocales: readonly L[];
  /** Native-script name per locale (English / Bahasa Malaysia / 中文 / தமிழ்). */
  readonly nativeNames: Readonly<Record<L, string>>;
  /**
   * Short form for the TRIGGER on narrow screens. Optional; falls back to the
   * native name, which is what every caller did before this existed.
   *
   * "Bahasa Malaysia" is 162px in the pill. At 360px that pushed the document
   * to 371px — an 11px horizontal overflow on every agent screen, in Malay
   * only. The popover beside it had already been capped for exactly this
   * reason, with a comment naming "Bahasa Malaysia" specifically; the trigger
   * was the half nobody had measured, because nobody had loaded the app in
   * Malay at 360px until the locale × width matrix did.
   *
   * The full name stays the accessible name, so shortening is a visual
   * economy and never a loss of information.
   */
  readonly shortNames?: Readonly<Record<L, string>>;
  /** True only on the very first launch — drives the nudge. Caller flips false on pick. */
  readonly firstLaunchDetected?: boolean;
  /** Localized copy for the first-launch nudge per locale. */
  readonly nudgeLines?: Readonly<Record<L, string>>;
}

export function LocaleSwitcher<L extends string>(props: LocaleSwitcherProps<L>) {
  const {
    locale,
    setLocale,
    supportedLocales,
    nativeNames,
    shortNames,
    firstLaunchDetected,
    nudgeLines,
  } = props;
  const narrow = useIsNarrow();
  const [open, setOpen] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  // The first time the menu opens after a detected launch, it explains itself.
  const [explainInMenu] = useState(() => Boolean(firstLaunchDetected && nudgeLines));
  const rootRef = useRef<HTMLDivElement>(null);
  const dismissNudge = useCallback(() => setNudgeDismissed(true), []);
  const showNudge = Boolean(firstLaunchDetected) && !nudgeDismissed && !open && Boolean(nudgeLines);
  // A hint, not a gate: the glow leaves by itself.
  useEffect(() => {
    if (!showNudge) return undefined;
    const id = window.setTimeout(dismissNudge, NUDGE_VISIBLE_MS);
    return () => window.clearTimeout(id);
  }, [showNudge, dismissNudge]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  function pick(next: L) {
    feel('tap');
    setLocale(next);
    setOpen(false);
    setNudgeDismissed(true);
  }

  function toggle() {
    feel('tap');
    setOpen((v) => !v);
    setNudgeDismissed(true);
  }

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 6,
      }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Language: ${nativeNames[locale]}`}
        aria-description={showNudge && nudgeLines ? nudgeLines[locale] : undefined}
        className={showNudge ? 'k-nudge-glow' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5em',
          /* em-based padding scales with the pill's font-size; min-height
             follows --touch-target token (44px → 52px) so the tap zone
             stays comfortable under accessibility font-scale. */
          padding: '0.5em 0.875em 0.5em 0.75em',
          minHeight: 'var(--touch-target, 2.75rem)',
          borderRadius: '999px',
          /* Pill background + border are THEME-AWARE so the pill re-skins with
             the surface its text does. Root cause of the prior light-on-light
             failure: text used `--color-on-surface` (flips to light on dark
             themes) while the pill was a hard-coded near-white. Now both derive
             from theme tokens:
               - `--glass-bg`  → the app's translucent glass (dark on dark
                 themes, light on light) — keeps the frosted look on marketing.
               - fallback `--color-surface` pairs with `--color-on-surface` by
                 design, so consumers without a glass token (find / dashboard /
                 agent, and any dark-surface white-label tenant) still get a
                 pill that contrasts with its own text.
             Both pairings clear WCAG AA (≥ 4.5:1) in light and dark. */
          border: '1px solid var(--glass-border, var(--hairline, var(--color-border-strong)))',
          background: 'var(--glass-bg, var(--color-surface, rgba(255,255,255,0.85)))',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 1px 2px var(--tint-subtle), 0 4px 12px var(--tint-subtle)',
          cursor: 'pointer',
          fontSize: 'var(--text-meta, 0.8125rem)',
          lineHeight: 1.3,
          fontWeight: 500,
          color: 'var(--color-on-surface, #0f172a)',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          // filter joins the list for the shared hover; see premium-motion.css.
          transition: 'transform 120ms ease, filter 120ms ease',
        }}
      >
        <GlobeIcon />
        <span>{narrow ? (shortNames?.[locale] ?? nativeNames[locale]) : nativeNames[locale]}</span>
        <span
          aria-hidden
          style={{
            fontSize: '0.75em',
            color: 'var(--color-on-surface-muted, #64748b)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 160ms ease',
          }}
        >
          {/* Decorative, non-translatable caret; expression form keeps it out
              of the jsx-text-only i18n rule. */}
          {'▾'}
        </span>
      </button>

      {/* The first-launch hint used to be a bubble floating under the pill.
          On a phone it covered the shop's name at the top of the home screen
          for its first six seconds — the moment a first impression forms — and
          the agent sign-in logo. Now the pill glows instead, and the sentence
          waits at the top of the menu for whoever taps it: nothing is covered. */}

      {open ? (
        <ul
          role="listbox"
          aria-label="Choose language"
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            right: 0,
            /* min-width in rem scales with a11y font-scale + max-width
               caps so very long native names (e.g., "Bahasa Malaysia")
               don't push the popover off a 360px screen. */
            minWidth: 'min(14rem, calc(100vw - 1.5rem))',
            maxWidth: 'calc(100vw - 1.5rem)',
            background: 'var(--color-surface, #ffffff)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.875rem',
            boxShadow:
              '0 1px 2px var(--tint-subtle),' +
              '0 12px 32px var(--color-border-strong),' +
              '0 24px 60px var(--color-border)',
            padding: '0.375rem',
            margin: 0,
            listStyle: 'none',
            zIndex: 30,
            display: 'grid',
            gap: '0.125rem',
          }}
        >
          {explainInMenu && nudgeLines ? (
            <li
              role="presentation"
              style={{
                padding: '0.5em 0.75em 0.625em',
                fontSize: 'var(--text-meta, 0.8125rem)',
                lineHeight: 1.4,
                color: 'var(--color-on-surface-muted, #64748b)',
                borderBottom: '1px solid var(--color-border)',
                marginBottom: '0.25rem',
              }}
            >
              {nudgeLines[locale]}
            </li>
          ) : null}
          {supportedLocales.map((l) => {
            const selected = l === locale;
            return (
              <li key={l}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => pick(l)}
                  style={{
                    width: '100%',
                    minHeight: 'var(--touch-target, 2.75rem)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75em',
                    padding: '0.625em 0.75em',
                    borderRadius: '0.625rem',
                    border: 'none',
                    /* Selected row is theme-aware too: `--color-primary` is a
                       fixed brand green that fails on dark surfaces (3.1:1 on
                       the dark popover). `--kilat-lume` is the theme's luminous
                       accent — bright green on dark, the brand green on light —
                       so the selected label clears AA in both (dark ≈ 9.8:1,
                       light ≈ 4.8:1). Non-Kilat consumers (no `--kilat-lume`)
                       fall back to their primary, unchanged. The tint stays a
                       deliberately faint 0.08 wash: strengthening it darkens the
                       light-theme row enough to drop the green label under 4.5. */
                    background: selected ? 'rgba(var(--color-primary-rgb), 0.08)' : 'transparent',
                    color: selected
                      ? 'var(--kilat-lume, var(--color-primary, #475569))'
                      : 'var(--color-on-surface, #0f172a)',
                    fontSize: 'var(--text-body, 1rem)',
                    lineHeight: 1.35,
                    fontWeight: selected ? 600 : 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span>{nativeNames[l]}</span>
                  {selected ? (
                    <span
                      aria-hidden
                      style={{
                        fontSize: '0.875em',
                        lineHeight: 1,
                        color: 'var(--kilat-lume, var(--color-primary, #475569))',
                      }}
                    >
                      {/* Decorative, non-translatable check glyph (expression form). */}
                      {'✓'}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** How long the first-launch language hint stays before leaving on its own. */
const NUDGE_VISIBLE_MS = 6_000;

/**
 * Short trigger labels for the four supported locales.
 *
 * Defined here, once, rather than as a sixth copy of a per-app map: these are
 * properties of the LANGUAGES, not of any app, and the five existing
 * `LOCALE_NATIVE_NAMES` duplicates are already one source of drift too many.
 *
 * Chosen so each stays in its own script and stays recognisable to a reader of
 * that language:
 *   · `en` — already short.
 *   · `ms` — "BM" is what Bahasa Malaysia is universally called in Malaysia.
 *     Truncating to "Bahasa Mala…" instead would be an ellipsis where a real
 *     word exists.
 *   · `zh` / `ta` — already two and five characters; unchanged.
 */
export const LOCALE_SHORT_NAMES = {
  en: 'English',
  ms: 'BM',
  zh: '中文',
  ta: 'தமிழ்',
} as const;

/**
 * True below the narrow breakpoint, where "Bahasa Malaysia" no longer fits.
 *
 * Decided HERE rather than in CSS, deliberately. The first attempt rendered two
 * spans and let a rule in `premium-motion.css` hide one — which worked in the
 * four apps that import that stylesheet and rendered "EnglishEnglish▾" in
 * `apps/marketing`, which does not. A shared component cannot depend on a
 * consumer having loaded a particular sheet; if it does, the failure shows up
 * as garbled text in exactly the app nobody checked.
 *
 * Defaults to FALSE — the full native name — so server-prerendered HTML and a
 * no-JS render both show the complete word. Narrowing is then a progressive
 * refinement rather than something a reader can be left stuck without.
 */
const NARROW_MAX_WIDTH = 479;

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(`(max-width: ${NARROW_MAX_WIDTH}px)`);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return narrow;
}
